import React from 'react';
import { PROJECTS, SOLUTIONS } from '../../constants/stats';
import { OUTSIDE_REGIONS, OVERSEAS } from './VisitMap';

/*
 * 실적 숫자 띠 — 메인 첫 화면과 회사소개(/about)가 같이 쓴다.
 * 숫자는 constants/stats.js 와 지도 데이터(VisitMap)에서 온다 — 손으로 적지 않는다.
 *
 * 이름과 설명 줄은 덩어리로 나눠 둔다. 좁은 화면(갤럭시 360px)이나 글자를 키운 폰에서
 * "2026년 / 10월", "PDA / 앱 5" 처럼 갈라지지 않고 덩어리 사이에서만 줄이 바뀌게 한다.
 * 구분 기호는 앞 덩어리 끝에 붙인다.
 */
const chunks = (parts, sep) => parts.map((p, i) => (i < parts.length - 1 ? `${p} ${sep}` : p));

export const STAT_ITEMS = {
    projects: {
        value: PROJECTS.count,
        unit: '건',
        label: ['프로젝트 수행'],
        note: chunks([PROJECTS.from, PROJECTS.to], '~'),
    },
    solutions: {
        value: SOLUTIONS.count,
        unit: '종',
        label: [`${SOLUTIONS.year}년`, '자체 개발 솔루션'],
        note: chunks(SOLUTIONS.groups.map((g) => `${g.label} ${g.count}`), '·'),
    },
    // 메인에는 바로 아래 '전국 방문' 섹션이 같은 숫자를 크게 보여 주므로 회사소개에서만 쓴다.
    regions: {
        value: OUTSIDE_REGIONS.length,
        unit: '곳',
        label: ['서울·경기 밖', '방문·설치 지역'],
        note: [`해외 ${OVERSEAS.join('·')}까지`],
    },
};

/** 덩어리 안에서는 줄을 바꾸지 않는다. */
const NoBreak = ({ parts }) => parts.map((part, i) => (
    <React.Fragment key={part}>
        {i > 0 && ' '}
        <span className="whitespace-nowrap">{part}</span>
    </React.Fragment>
));

/*
 * 둘이면 폰에서도 두 칸으로 나란히. 셋이면 폰에서는 세로로 쌓는다 — 360px 에 세 칸을 넣으면
 * 한 칸이 100px 남짓이라 "2026년 자체 개발 솔루션" 이 세 줄로 부서진다.
 */
const LAYOUT = {
    2: { list: 'max-w-xl grid-cols-2 divide-x', item: 'px-1' },
    3: { list: 'max-w-3xl grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x', item: 'px-1 py-5 first:pt-0 last:pb-0 sm:py-0' },
};

const StatsBand = ({ items, className = '' }) => {
    const layout = LAYOUT[items.length] ?? LAYOUT[2];
    return (
        <dl className={`mx-auto grid divide-gray-200 ${layout.list} ${className}`}>
            {items.map(({ value, unit, label, note }) => (
                <div key={label.join(' ')} className={`flex flex-col items-center ${layout.item}`}>
                    <dt className="mt-3 text-sm md:text-base font-bold text-gray-700 tracking-tight">
                        <NoBreak parts={label} />
                    </dt>
                    {/* 눈은 숫자를 먼저 읽고, 화면낭독기는 이름을 먼저 읽는다. */}
                    <dd className="order-first text-4xl/none md:text-5xl/none font-extrabold text-gray-900 tracking-tight">
                        {value}
                        <span className="ml-0.5 text-xl md:text-2xl font-bold text-gray-600">{unit}</span>
                    </dd>
                    <dd className="mt-1 text-xs/relaxed md:text-sm/relaxed text-gray-500">
                        <NoBreak parts={note} />
                    </dd>
                </div>
            ))}
        </dl>
    );
};

export default StatsBand;
